
import { Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface PropertyAgentProps {
  name: string;
  company: string;
  email: string;
  phone: string;
  photoUrl?: string;
  website?: string;
}

const PropertyAgent = ({
  name,
  company,
  phone,
  email,
  photoUrl,
  website
}: PropertyAgentProps) => {
  // Generate initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  // Format phone for WhatsApp link (remove spaces, +, etc.)
  const whatsappNumber = phone.replace(/\s+|\+/g, '');

  return (
    <Card className="border border-propertylk-blue/10 overflow-hidden shadow-md">
      <CardHeader className="bg-gradient-to-r from-propertylk-blue to-propertylk-darkblue text-white p-4">
        <CardTitle className="text-lg font-bold">Contact Agent</CardTitle>
      </CardHeader>
      <CardContent className="pt-6 bg-gradient-to-br from-white to-propertylk-lightblue/30">
        <div className="flex items-center gap-4 mb-4">
          {/* Agent Photo or Initials */}
          {photoUrl ? (
            <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-propertylk-blue/20">
              <img 
                src={photoUrl} 
                alt={name} 
                className="h-full w-full object-cover" 
              />
            </div>
          ) : (
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-propertylk-blue to-propertylk-darkblue flex items-center justify-center text-white font-bold text-xl border-2 border-white/20">
              {getInitials(name)}
            </div>
          )}
          
          {/* Agent Info */}
          <div>
            <h3 className="font-semibold text-lg text-propertylk-darkblue">{name}</h3>
            <p className="text-gray-600">{company}</p>
          </div>
        </div>
        
        {/* Contact Buttons - Now displayed side by side */}
        <div className="grid grid-cols-2 gap-3">
          {/* Call Button */}
          <a 
            href={`tel:${phone.replace(/\s+/g, '')}`} 
            className="w-full"
          >
            <Button 
              className="w-full flex items-center justify-center gap-2 bg-propertylk-blue hover:bg-propertylk-darkblue transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call
            </Button>
          </a>
          
          {/* WhatsApp Button */}
          <a 
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer" 
            className="w-full"
          >
            <Button 
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-green-600 text-green-600 hover:bg-green-50 transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              WhatsApp
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyAgent;
